import { Loader2, Save } from 'lucide-react'
import { useState } from 'react'
import { useTaxesList } from '@/features/taxes/hooks/useTaxes'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { TaxSelector } from './TaxSelector'

export interface TaxAssignmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentTaxIds?: string[]
  entityType: 'customer' | 'organization'
  entityName?: string
  onAssign: (data: {
    tax_ids: string[]
    replace_existing: boolean
  }) => Promise<void>
  isLoading?: boolean
}

export function TaxAssignmentDialog({
  open,
  onOpenChange,
  currentTaxIds = [],
  entityType,
  entityName,
  onAssign,
  isLoading = false,
}: TaxAssignmentDialogProps) {
  const { taxes } = useTaxesList({ is_active: true })
  const [selection, setSelection] = useState<{
    tax_ids: string[]
    replace_existing: boolean
  }>({
    tax_ids: currentTaxIds,
    replace_existing: false,
  })

  const handleAssign = async () => {
    await onAssign(selection)
    onOpenChange(false)
  }

  const selectedTaxes = selection.tax_ids
    .map(id => taxes.find(t => t.id === id))
    .filter(t => t !== undefined)

  const currentTaxes = currentTaxIds
    .map(id => taxes.find(t => t.id === id))
    .filter(t => t !== undefined)

  const hasChanges =
    selection.tax_ids.length !== currentTaxIds.length ||
    !selection.tax_ids.every((id, index) => id === currentTaxIds[index])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            Assign Taxes to{' '}
            {entityType === 'customer' ? 'Customer' : 'Organization'}
          </DialogTitle>
          <DialogDescription>
            {entityName && (
              <span className='block mb-2'>
                Assigning taxes to: <strong>{entityName}</strong>
              </span>
            )}
            {entityType === 'customer' ? (
              <span>
                Select taxes to apply to this customer. Customer-specific taxes
                override organization defaults.
              </span>
            ) : (
              <span>
                Select default taxes for this organization. These will apply to
                all customers in the organization unless overridden by
                customer-specific taxes.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Current taxes summary */}
          {currentTaxes.length > 0 && (
            <div className='rounded-lg border bg-muted/50 p-4'>
              <h4 className='text-sm font-medium mb-2'>
                Current Taxes ({currentTaxes.length})
              </h4>
              <div className='flex flex-wrap gap-2'>
                {currentTaxes.map(tax => (
                  <Badge key={tax.id} variant='secondary'>
                    {tax.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Tax selector */}
          <TaxSelector
            selectedTaxIds={currentTaxIds}
            onSelectionChange={setSelection}
            replaceExisting={false}
          />

          {/* Preview changes */}
          {hasChanges && (
            <div className='rounded-lg border bg-blue-50 dark:bg-blue-950 p-4'>
              <h4 className='text-sm font-medium mb-2 text-blue-900 dark:text-blue-100'>
                Preview: {selection.replace_existing ? 'Replacing' : 'Adding'}{' '}
                Taxes
              </h4>
              <div className='space-y-2'>
                {selection.replace_existing && currentTaxes.length > 0 && (
                  <div>
                    <p className='text-xs text-blue-700 dark:text-blue-300 mb-1'>
                      Will remove:
                    </p>
                    <div className='flex flex-wrap gap-1'>
                      {currentTaxes.map(tax => (
                        <Badge
                          key={tax.id}
                          variant='outline'
                          className='text-xs'
                        >
                          {tax.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {selectedTaxes.length > 0 && (
                  <div>
                    <p className='text-xs text-blue-700 dark:text-blue-300 mb-1'>
                      Will {selection.replace_existing ? 'set to' : 'add'}:
                    </p>
                    <div className='flex flex-wrap gap-1'>
                      {selectedTaxes.map((tax, index) => (
                        <Badge
                          key={tax.id}
                          variant='default'
                          className='text-xs gap-1'
                        >
                          <span className='opacity-70'>#{index + 1}</span>
                          {tax.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!hasChanges || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Assigning...
              </>
            ) : (
              <>
                <Save className='mr-2 h-4 w-4' />
                Assign {selectedTaxes.length}{' '}
                {selectedTaxes.length === 1 ? 'Tax' : 'Taxes'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
